/**
 * Reading longitudes the short way round.
 *
 * Three Aleutian marine zones straddle 180 degrees, and every naive treatment
 * of them describes the whole planet instead. A zone with points at -179.9 and
 * +179.9 spans four tenths of a degree of water; measured as `max - min` it
 * spans 359.8, so a fit centred on it lands in the Atlantic and a polygon drawn
 * from it stretches across the world.
 *
 * The fix everywhere is the same: read the longitudes on a 0..360 dial when
 * that is the narrower way round, and let coordinates past 180 stand. MapLibre
 * accepts them and wraps them itself.
 */

export type Box = [west: number, south: number, east: number, north: number];

/**
 * The same longitudes, shifted onto whichever dial holds them more tightly.
 *
 * Returns them unchanged when -180..180 already does, so nothing moves for the
 * ninety-odd percent of the world that never touches the dateline.
 */
export function unwrapLongitudes(lons: readonly number[]): number[] {
  if (lons.length === 0) return [];

  const plain = Math.max(...lons) - Math.min(...lons);
  const shifted = lons.map((x) => (x < 0 ? x + 360 : x));
  const shiftedSpan = Math.max(...shifted) - Math.min(...shifted);

  return shiftedSpan < plain ? shifted : [...lons];
}

/**
 * One box around several, by the short way round.
 *
 * The east edge may come back greater than 180, which is deliberate: it says
 * "continue past the dateline" rather than "go the other way".
 */
export function unionBounds(boxes: readonly Box[]): Box | null {
  if (boxes.length === 0) return null;

  // A box already stored wrapped — west past east — carries its east edge on.
  const ends = boxes.map(([w, , e]) => (e < w ? e + 360 : e));
  const lons = unwrapLongitudes([...boxes.map(([w]) => w), ...ends]);

  const half = lons.length / 2;
  const west = Math.min(...lons.slice(0, half));
  const east = Math.max(...lons.slice(half));

  const south = Math.min(...boxes.map(([, s]) => s));
  const north = Math.max(...boxes.map(([, , , n]) => n));

  return [west, south, east, north];
}

/** Every coordinate pair in a GeoJSON geometry, however deeply nested. */
function* coords(node: unknown): Generator<number[]> {
  const arr = node as unknown[];
  if (typeof arr[0] === 'number') yield arr as number[];
  else for (const child of arr) yield* coords(child);
}

/**
 * A geometry whose rings do not double back across the dateline.
 *
 * Only rings that actually straddle it are touched; everything else is handed
 * back as it came, so this is safe to run over the whole country.
 */
export function unwrapGeometry<T extends { type: string; coordinates: unknown }>(geometry: T): T {
  const lons = [...coords(geometry.coordinates)].map(([x]) => x);
  if (lons.length === 0) return geometry;
  if (Math.max(...lons) - Math.min(...lons) <= 180) return geometry;

  const shift = (node: unknown): unknown => {
    const arr = node as unknown[];
    if (typeof arr[0] === 'number') {
      const [x, y] = arr as number[];
      return [x < 0 ? x + 360 : x, y];
    }
    return arr.map(shift);
  };

  return { ...geometry, coordinates: shift(geometry.coordinates) };
}
